import { Divider, Table } from 'antd'
import { SortOrder } from 'antd/lib/table'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { ExecutedPaymentDto } from '../../../../../integration-tests/common/types'
import { PerEmpty } from '../../../components/custom_components/Empty'
import { LoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors'
import { PerTag } from '../../../components/custom_components/Tag'
import PerText from '../../../components/custom_components/Text'
import useCompleteExecutedPaymentHistory from '../../../graphql/contractHistory/hooks/useCompleteExecutedPaymentHistory'
import { useAmountAndCurrency } from '../../../shared/useAmountAndCurrency'
import { useDateTime } from '../../../shared/useDateTime'
import { ExecutedPaymentType } from '../../../types'
import { TimeZoneAndLocale } from '../../../types.custom'

type PaymentHistoryTableProps = {
  employeeId: string
  noDivider?: boolean
  resigned?: boolean
  timezone: TimeZoneAndLocale
}

export const PaymentHistoryTable: FC<PaymentHistoryTableProps> = ({ employeeId, noDivider = false, timezone, resigned }) => {
  const { t } = useTranslation()
  const { formatCurrency } = useAmountAndCurrency(timezone.locale)
  const { formatDateMonthYearShort, compareDates } = useDateTime(timezone)

  // fetch data here
  const { data, loading, error } = useCompleteExecutedPaymentHistory(employeeId, resigned)

  if (loading || error || !data) {
    return <LoadingAndErrors isLoading={loading} hasNoData={!data} error={error} />
  }

  const payments: ExecutedPaymentDto[] = data.excecutedPaymentsByEmployeeId.executedPayments
  const showPayments = payments && payments.length > 0

  const typeColor = (type: ExecutedPaymentType) => {
    if (type === ExecutedPaymentType.OneOff) {
      return 'red'
    } else if (type === ExecutedPaymentType.CarAllowance) {
      return 'gold'
    } else if (type === ExecutedPaymentType.Retention) {
      return 'pink'
    } else if (type === ExecutedPaymentType.TargetBonus) {
      return 'cyan'
    } else if (type === ExecutedPaymentType.TargetBonusPrepayment) {
      return 'blue'
    } else if (type === ExecutedPaymentType.ValuableHours) {
      return 'lime'
    } else if (type === ExecutedPaymentType.VariableSalaryComponent) {
      return 'volcano'
    } else if (type === ExecutedPaymentType.Raf) {
      return 'purple'
    } else if (type === ExecutedPaymentType.Warrants) {
      return 'magenta'
    } else {
      return undefined // fallback
    }
  }

  const paymentColumns = [
    {
      key: 'paymentHistory-referenceYear',
      title: t('profile.contract.payment.paymentDate'),
      dataIndex: 'referenceYear',
      className: 'textAlign-left minWidth-column-xl',
      defaultSortOrder: 'descend' as SortOrder,
      sorter: (dateA: ExecutedPaymentDto, dateB: ExecutedPaymentDto) => {
        const a = dateA.referenceYear ? dateA.referenceYear : 1970
        const b = dateB.referenceYear ? dateB.referenceYear : 1970
        return a - b
      },
      render: (dataIndex: ExecutedPaymentDto) => <PerText>{dataIndex}</PerText>,
    },
    {
      key: 'paymentHistory-paymentDate',
      title: t('profile.contract.payment.paymentMonthDate'),
      className: 'textAlign-left minWidth-column-m',
      defaultSortOrder: 'descend' as SortOrder,
      sorter: (dateA: ExecutedPaymentDto, dateB: ExecutedPaymentDto) => {
        const a = dateA.paymentDate ? dateA.paymentDate : ' '
        const b = dateB.paymentDate ? dateB.paymentDate : ' '
        return compareDates(a, b)
      },
      render: (dataIndex: ExecutedPaymentDto) => <PerText>{formatDateMonthYearShort(dataIndex.paymentDate, true)}</PerText>,
    },
    {
      key: 'paymentHistory-type',
      title: t('profile.contract.payment.type'),
      className: 'textAlign-left minWidth-column-m',
      dataIndex: 'type',
      sorter: (typeA: ExecutedPaymentDto, typeB: ExecutedPaymentDto) => {
        const a = typeA.type ? typeA.type : ' '
        const b = typeB.type ? typeB.type : ' '
        return a.localeCompare(b)
      },
      render: (dataIndex: ExecutedPaymentType) => <PerTag color={typeColor(dataIndex)}>{t(`profile.executedPayment.${dataIndex}`)}</PerTag>,
    },
    {
      key: 'paymentHistory-amount',
      title: t('profile.contract.payment.amount'),
      className: 'textAlign-right minWidth-column-m',
      sorter: (amountA: ExecutedPaymentDto, amountB: ExecutedPaymentDto) => {
        const a = amountA.amount ? amountA.amount : 0
        const b = amountB.amount ? amountB.amount : 0
        return a - b
      },
      render: (row: ExecutedPaymentDto) => <PerText>{formatCurrency(row.amount, row.currency)}</PerText>,
    },
  ]

  return (
    <>
      {showPayments && (
        <>
          {!noDivider && <Divider />}
          <PerText appearance="tile-heading" mb="s">
            {t('profile.contract.payment.header')}
          </PerText>
          <Table
            dataSource={payments ? payments.map((paymentHistory) => ({ ...paymentHistory, key: paymentHistory.id })) : []}
            className="small"
            columns={paymentColumns}
            pagination={false}
            locale={{
              emptyText: <PerEmpty info={t('profile.contract.payment.emptyTable')} />,
            }}
          />
        </>
      )}
    </>
  )
}
